import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as api from "./api";
import type {
  GuessRow,
  Identity,
  PlayerView,
  ReactionEvent,
  RoomState,
  Settings,
} from "./types";

export type ConnectionStatus = "idle" | "connecting" | "open" | "closed";

export type Outbound =
  | { type: "settings"; patch: Partial<Settings> }
  | { type: "round:start" }
  | { type: "round:end" }
  | { type: "hint"; kind: "category" | "definition" }
  | { type: "game:end" }
  | { type: "game:reset" }
  | { type: "guess"; value: string }
  | { type: "react"; emoji: string };

export type Toast = { id: number; text: string };

type Store = {
  identity: Identity | null;
  room: RoomState | null;
  status: ConnectionStatus;
  error: string | null;
  toast: Toast | null;
  /** Bumped every time a guess bounces, so the row can shake again. */
  shakeSeq: number;
  /** Row index the server just marked. Drives the flip. */
  revealIndex: number | null;
  /** True between sending a guess and hearing back. Input is locked. */
  pending: boolean;
  reactions: ReactionEvent[];
  me: PlayerView | null;
  isHost: boolean;
  setIdentity: (identity: Identity | null) => void;
  connect: (code: string) => void;
  disconnect: () => void;
  send: (message: Outbound) => void;
  guess: (word: string) => void;
  say: (text: string) => void;
};

const Ctx = createContext<Store | null>(null);

let toastSeq = 0;

export function StoreProvider({ children }: { children: ReactNode }) {
  const [identity, setIdentityState] = useState<Identity | null>(null);
  const [room, setRoom] = useState<RoomState | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [shakeSeq, setShakeSeq] = useState(0);
  const [revealIndex, setRevealIndex] = useState<number | null>(null);
  const [pending, setPending] = useState(false);
  const [reactions, setReactions] = useState<ReactionEvent[]>([]);

  const socket = useRef<WebSocket | null>(null);
  /** Latest board outside React state, so a marked row can be folded in fast. */
  const boardRef = useRef<GuessRow[]>([]);
  const wanted = useRef<string | null>(null);
  const retry = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** When the socket last heard anything at all, heartbeats included. */
  const lastSeen = useRef(0);
  /** Consecutive failed attempts, for backoff. Reset the moment one opens. */
  const attempts = useRef(0);

  // A stored token means a refresh comes back as the same person, with score.
  useEffect(() => {
    api.restoreSession().then((found) => {
      if (found) setIdentityState(found);
    });
  }, []);

  const say = useCallback((text: string) => {
    setToast({ id: ++toastSeq, text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 1800);
  }, []);

  const open = useCallback(
    (code: string) => {
      // A socket stuck in CONNECTING is not a live socket. Ten seconds without
      // an open event means the attempt is dead and a fresh one is due.
      const current = socket.current;
      if (current) {
        if (current.readyState === WebSocket.OPEN) return;
        if (current.readyState === WebSocket.CONNECTING) {
          if (Date.now() - lastSeen.current < 10_000) return;
          try {
            current.close();
          } catch {
            /* already gone */
          }
        }
      }

      setStatus("connecting");
      lastSeen.current = Date.now();
      const ws = new WebSocket(api.socketUrl(code));
      socket.current = ws;

      ws.onopen = () => {
        attempts.current = 0;
        lastSeen.current = Date.now();
        setStatus("open");
        setError(null);
      };

      ws.onmessage = (event) => {
        lastSeen.current = Date.now();
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        switch (data.type) {
          // Server liveness beat. Nothing to draw; being received is the point.
          case "heartbeat":
            return;

          case "state":
            boardRef.current = (data.room as RoomState).board;
            setRoom(data.room as RoomState);
            return;

          case "tick":
            setRoom((prev) => (prev ? { ...prev, secondsLeft: data.secondsLeft } : prev));
            return;

          case "round:start":
            boardRef.current = [];
            setRevealIndex(null);
            setPending(false);
            return;

          /**
           * The marked row can beat the full state push, so it is folded in
           * here and the row index is remembered: that is the row that flips.
           */
          case "guess:result": {
            setPending(false);
            const known = boardRef.current;
            const already = known.some((r) => r.word === data.row.word && r.at === data.row.at);
            const board = already ? known : [...known, data.row];
            boardRef.current = board;
            setRevealIndex(board.length - 1);
            setRoom((prev) =>
              prev ? { ...prev, board, offListLeft: data.offListLeft ?? prev.offListLeft } : prev,
            );

            // Spending an allowance is the one thing worth saying out loud.
            if (data.row.wild) {
              const left = data.offListLeft ?? 0;
              say(
                left === 0
                  ? "No made-up words left"
                  : `${left} more made-up ${left === 1 ? "word" : "words"}`,
              );
            }
            return;
          }

          case "guess:rejected":
            setPending(false);
            setShakeSeq((n) => n + 1);
            say(String(data.reason));
            return;

          case "round:end":
            setPending(false);
            return;

          case "hint":
            say(
              data.kind === "definition"
                ? `Definition: ${data.definition}`
                : `Category: ${data.category}`,
            );
            return;

          case "react": {
            const reaction = data.reaction as ReactionEvent;
            setReactions((prev) => [...prev, reaction]);
            setTimeout(() => {
              setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
            }, 1700);
            return;
          }

          case "error":
            say(String(data.message));
            return;

          default:
            return;
        }
      };

      ws.onclose = (event) => {
        socket.current = null;
        setStatus("closed");
        // 4000-range codes are refusals from our own handler. Do not retry them.
        if (event.code >= 4000 && event.code < 5000) {
          setError(event.reason || "The room refused the connection.");
          wanted.current = null;
          return;
        }
        if (!wanted.current) return;

        // Back off a little so a server restart is not hammered, but stay
        // quick: the first few retries are what a player actually waits out.
        attempts.current += 1;
        const delay = Math.min(400 * 2 ** (attempts.current - 1), 5_000);
        const jitter = Math.random() * 250;
        if (retry.current) clearTimeout(retry.current);
        retry.current = setTimeout(() => open(wanted.current!), delay + jitter);
      };
    },
    [say],
  );

  /**
   * The reason a dropped player used to have to reload. A socket can stop
   * carrying traffic without ever firing `close` — laptop lid, wifi handover,
   * a proxy quietly dropping the connection. The server beats every 20s, so
   * silence past 50s means the socket is dead no matter what it claims, and
   * closing it by hand starts the normal reconnect. Coming back to the tab or
   * regaining a network reconnects immediately rather than waiting out a
   * backoff.
   */
  useEffect(() => {
    const check = () => {
      if (!wanted.current) return;
      const ws = socket.current;

      if (ws && ws.readyState === WebSocket.OPEN) {
        const silent = Date.now() - lastSeen.current;
        if (silent > 50_000) {
          try {
            ws.close();
          } catch {
            /* already gone */
          }
          return;
        }
        // Poke the server so silence is proof of a dead link, not a quiet room.
        if (silent > 25_000) ws.send(JSON.stringify({ type: "ping" }));
        return;
      }

      if (!ws) open(wanted.current);
    };

    const wake = () => {
      if (!wanted.current) return;
      if (document.visibilityState === "hidden") return;
      attempts.current = 0;
      if (retry.current) clearTimeout(retry.current);
      check();
      if (!socket.current) open(wanted.current);
    };

    const timer = setInterval(check, 5_000);
    window.addEventListener("online", wake);
    window.addEventListener("focus", wake);
    document.addEventListener("visibilitychange", wake);

    return () => {
      clearInterval(timer);
      window.removeEventListener("online", wake);
      window.removeEventListener("focus", wake);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [open]);

  const connect = useCallback(
    (code: string) => {
      wanted.current = code;
      attempts.current = 0;
      api.store.setRoom(code);
      open(code);
    },
    [open],
  );

  const disconnect = useCallback(() => {
    wanted.current = null;
    if (retry.current) clearTimeout(retry.current);
    retry.current = null;
    socket.current?.close(1000, "left");
    socket.current = null;
    setRoom(null);
    setStatus("idle");
    api.store.clearRoom();
  }, []);

  useEffect(
    () => () => {
      if (retry.current) clearTimeout(retry.current);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      socket.current?.close();
    },
    [],
  );

  const send = useCallback((message: Outbound) => {
    const ws = socket.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(message));
  }, []);

  const guess = useCallback(
    (word: string) => {
      setPending(true);
      send({ type: "guess", value: word });
    },
    [send],
  );

  const setIdentity = useCallback((next: Identity | null) => {
    setIdentityState(next);
    if (!next) api.store.clearToken();
  }, []);

  const me = useMemo(
    () => room?.players.find((p) => p.username === identity?.username) ?? null,
    [room, identity],
  );

  const value = useMemo<Store>(
    () => ({
      identity,
      room,
      status,
      error,
      toast,
      shakeSeq,
      revealIndex,
      pending,
      reactions,
      me,
      isHost: !!identity?.isAdmin,
      setIdentity,
      connect,
      disconnect,
      send,
      guess,
      say,
    }),
    [
      identity,
      room,
      status,
      error,
      toast,
      shakeSeq,
      revealIndex,
      pending,
      reactions,
      me,
      setIdentity,
      connect,
      disconnect,
      send,
      guess,
      say,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStore() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useStore must be used inside <StoreProvider>");
  return ctx;
}
